import Modal from "@/common/components/Modal";
import SmallBadge from "@/common/components/SmallBadge";
import useModalMessagesSimple from "@/common/hooks/useModalMessagesSimple";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { cn } from "@/common/utils/cn";
import { useQueryClient } from "@tanstack/react-query";
import { BellOffIcon } from "lucide-react";
import { useId, useState } from "react";
import { Tooltip } from "react-tooltip";
import { useMaintenanceEstablishmentChoiceAuctionEntryUnfollow } from "../../../hooks/useMaintenanceEstablishmentChoiceAuctionEntryUnfollow";

interface UnfollowAuctionProps {
  auctionEntryId: string | undefined;
}

const UnfollowAuction = ({
  auctionEntryId,
}: UnfollowAuctionProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const tooltipId = useId();

  const queryClient = useQueryClient();

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();

  const hasUnfollowPermission = !isUserPermissionsPending
    ? hasUserPermission('Establishment::Maintenance::EstablishmentChoice::AuctionEntries::Unfollow')
    : true;

  const {
    setMessages
  } = useModalMessagesSimple({
    isError: true,
  });

  const {
    mutate: unfollow,
    isPending: followIsPending,
  } = useMaintenanceEstablishmentChoiceAuctionEntryUnfollow({
    auctionEntryId,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['maintenanceEstablishmentChoiceAuctionEntryUpdatedSummary', auctionEntryId],
        });

        setIsOpen(false);
      },
      onError: (error) => {
        setMessages(error, { errorResponse: true });
      }
    }
  });

  return (
    <>
      <SmallBadge
        data-tooltip-id={!hasUnfollowPermission && !isUserPermissionsPending ? tooltipId : undefined}
        LeftIcon={BellOffIcon}
        text="Parar de acompanhar"
        className={cn(
          "transition-colors",
          hasUnfollowPermission
            ? "bg-danger hover:bg-[#E03E1AAA] cursor-pointer"
            : "bg-danger opacity-50 cursor-default"
        )}
        onClick={hasUnfollowPermission ? () => setIsOpen(true) : undefined}
      />

      {!hasUnfollowPermission && !isUserPermissionsPending && (
        <Tooltip id={tooltipId}>Você não tem permissão</Tooltip>
      )}

      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        bodyClassName='max-h-[calc(90vh-72px-48px)] overflow-auto'
        title="Você tem certeza?"
        subtitle="Você não irá mais acompanhar as atualizações desse leilão"
        titleClass="text-2xl"
        subtitleClassName="text-base mt-1"
        useCancellableFooter
        className="rounded-lg overflow-hidden"
        cancelButtonClassName="bg-white-100 rounded-es-lg"
        confirmButtonClassName="bg-primary-blue rounded-ee-lg"
        confirmButtonIsLoading={followIsPending}
        disabled={followIsPending || !auctionEntryId}
        showClose={false}
        onConfirm={() => unfollow()}
      />
    </>
  );
}

export default UnfollowAuction;
